// Filter state for the complaint lists lives in the query string rather than in
// component state. Opening a complaint routes away from the list, which unmounts
// it, so local state cannot survive the round trip -- the search the user just
// typed is gone by the time they come back. The URL is the one thing that does
// survive: both the browser Back button and the detail page's own back arrow
// (navigate(-1)) restore it, and it makes a filtered view shareable on top.
import { useEffect } from 'react';

export const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
export const DEFAULT_ROWS_PER_PAGE = 10;

// Pagination is 0-based in component state but 1-based in the URL, so a link
// someone pastes to a colleague reads the way they'd expect.
export const readPageParam = (searchParams) => {
  const parsed = parseInt(searchParams.get('page'), 10);
  return Number.isNaN(parsed) || parsed < 1 ? 0 : parsed - 1;
};

export const readRowsParam = (searchParams) => {
  const parsed = parseInt(searchParams.get('rows'), 10);
  return ROWS_PER_PAGE_OPTIONS.includes(parsed) ? parsed : DEFAULT_ROWS_PER_PAGE;
};

// True when the URL arrives carrying at least one active filter, which is the
// cue to open the filter panel: leaving it collapsed makes the list look
// mysteriously short with the search box hidden inside it.
export const hasActiveFilterParams = (searchParams, keys) =>
  keys.some((key) => searchParams.get(key));

// Mirrors the live filter values back into the query string. Empty values are
// dropped so an unfiltered list keeps a clean URL. Replacing rather than pushing
// keeps every keystroke in the search box out of the history stack, so Back
// still leaves the page instead of undoing the search one character at a time.
export const useFilterParamSync = (searchParams, setSearchParams, values, page, rowsPerPage) => {
  const next = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value) next.set(key, value);
  });
  if (page > 0) next.set('page', String(page + 1));
  if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) next.set('rows', String(rowsPerPage));

  // Compared as strings so the effect depends on the query itself, not on the
  // identity of the freshly built object, and so a no-op render never navigates.
  const nextQuery = next.toString();
  const currentQuery = searchParams.toString();

  useEffect(() => {
    if (nextQuery !== currentQuery) {
      setSearchParams(nextQuery, { replace: true });
    }
  }, [nextQuery, currentQuery, setSearchParams]);
};

// A page number restored from the URL can outrun the data -- rows deleted since
// the link was made, or a filter that narrows the result set -- which would land
// the user on a blank page. Waiting for the load to finish matters: clamping
// against an empty list mid-fetch would throw the restored page away every time.
export const useClampedPage = (loading, totalRows, rowsPerPage, page, setPage) => {
  useEffect(() => {
    if (loading) return;
    const lastPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [loading, totalRows, rowsPerPage, page, setPage]);
};
